import type { TeamScorecardSummaryDto } from '@internal/scorecards-common';
import { mapGroupEntitiesToTeams, useLogger } from '@internal/ui-common';
import { useEffect, useMemo, useReducer } from 'react';

import type { Entity } from '@backstage/catalog-model';
import { parseEntityRef } from '@backstage/catalog-model';
import { scorecardsApiRef } from '../api/ScorecardsApi';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

export interface TeamScorecardState {
  loading: boolean;
  error?: Error;
  /** GitLab topic of the team, e.g. `team-bhabha`; undefined for non-team groups. */
  team?: string;
  summary?: TeamScorecardSummaryDto;
}

/**
 * `Team` strips the division from the group name using `spec.parent`, and
 * expects a bare name (`teams-scalar`). The catalog may hold a full ref
 * (`group:default/teams-scalar`), so reduce it to its name first.
 */
function withBareParent(entity: Entity): Entity {
  const parent = (entity.spec as { parent?: unknown } | undefined)?.parent;
  if (typeof parent !== 'string') return entity;
  try {
    const { name } = parseEntityRef(parent, {
      defaultKind: 'group',
      defaultNamespace: 'default',
    });
    return { ...entity, spec: { ...entity.spec, parent: name } };
  } catch {
    return entity;
  }
}

export function resolveTeamTopic(
  entity: Entity,
  logger: ReturnType<typeof useLogger>,
): string | undefined {
  const slug = mapGroupEntitiesToTeams([withBareParent(entity)], logger)[0]
    ?.nameInGitlab;
  return slug ? `team-${slug}` : undefined;
}

type FetchState = Omit<TeamScorecardState, 'team'>;
type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; summary: TeamScorecardSummaryDto }
  | { type: 'FETCH_ERROR'; error: Error }
  | { type: 'NO_TEAM' };

function reducer(state: FetchState, action: Action): FetchState {
  switch (action.type) {
    case 'FETCH_START':
      return { loading: true };
    case 'FETCH_SUCCESS':
      return { loading: false, summary: action.summary };
    case 'FETCH_ERROR':
      return { loading: false, error: action.error };
    case 'NO_TEAM':
      return { loading: false };
    default:
      return state;
  }
}

export function useTeamScorecard(): TeamScorecardState {
  const api = useApi(scorecardsApiRef);
  const { entity } = useEntity();
  const logger = useLogger();
  const team = useMemo(
    () => resolveTeamTopic(entity, logger),
    [entity, logger],
  );

  const [state, dispatch] = useReducer(reducer, { loading: true });

  useEffect(() => {
    if (!team) {
      dispatch({ type: 'NO_TEAM' });
      return undefined;
    }

    let cancelled = false;
    dispatch({ type: 'FETCH_START' });

    api
      .getTeamSummary(team)
      .then(summary => {
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', summary });
      })
      .catch(err => {
        if (!cancelled)
          dispatch({
            type: 'FETCH_ERROR',
            error: err instanceof Error ? err : new Error(String(err)),
          });
      });

    return () => {
      cancelled = true;
    };
  }, [api, team]);

  return { ...state, team };
}
